import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Rocket, User, PenLine, FolderOpen, CheckCircle2, Store, Coins, 
  CreditCard, HelpCircle 
} from 'lucide-react';

const Guide = () => {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);

  const categories = [
    { id: 'getting-started', label: 'Démarrage', icon: <Rocket size={18} /> },
    { id: 'user', label: 'Utilisateur', icon: <User size={18} /> },
    { id: 'author', label: 'Auteur/Gérant', icon: <PenLine size={18} /> },
    { id: 'projects', label: 'Projets', icon: <FolderOpen size={18} /> },
    { id: 'tasks', label: 'Missions', icon: <CheckCircle2 size={18} /> },
    { id: 'shop', label: 'Boutique', icon: <Store size={18} /> },
    { id: 'finance', label: 'Finances', icon: <Coins size={18} /> },
    { id: 'subscription', label: 'Abonnements', icon: <CreditCard size={18} /> },
    { id: 'faq', label: 'FAQ', icon: <HelpCircle size={18} /> },
  ];

  const allSections = {
    'getting-started': [
      {
        title: "Qu'est-ce que DJAMANN ?",
        content: `DJAMANN est une plateforme innovante qui permet à chacun de lancer son business et de collaborer avec d'autres, même sans capital initial.

🎯 Notre mission :
Démocratiser l'entrepreneuriat en permettant à toute personne motivée de créer, développer et gérer un projet professionnel.

💡 Concept clé :
Vous avez une idée mais pas de moyens ? Trouvez des collaborateurs qui apportent leurs compétences, leur temps ou leurs ressources en échange d'une part du projet.

🌍 Pour qui ?
• Entrepreneurs débutants ou confirmés
• Freelances cherchant des missions
• Étudiants souhaitant acquérir de l'expérience
• Professionnels cherchant des associés
• Toute personne avec une idée à concrétiser`
      },
      {
        title: "Créer un compte",
        content: `La création de compte est simple et gratuite :

📝 Étapes :
1. Cliquez sur "S'inscrire" ou "Créer un compte"
2. Renseignez vos informations :
   • Nom complet
   • Adresse email (sera votre identifiant)
   • Mot de passe sécurisé
   • Date de naissance (16 ans minimum)
3. Acceptez les conditions d'utilisation
4. Validez votre inscription

✅ Après inscription :
• Accès immédiat au feed "Pour Toi"
• Possibilité de postuler aux offres
• Complétez votre profil pour plus de visibilité

💡 Conseil : Utilisez une adresse email que vous consultez régulièrement pour ne pas manquer les notifications importantes.`
      },
      {
        title: "Comprendre les rôles",
        content: `DJAMANN fonctionne avec deux rôles principaux :

👤 UTILISATEUR STANDARD
C'est le rôle de base, obtenu à l'inscription.

Permet de :
• Consulter le feed "Pour Toi" (toutes les opportunités)
• Postuler à des offres (emploi, stage, collaboration, prestation)
• Créer un profil professionnel attractif
• Échanger avec les auteurs de projets

✍️ AUTEUR / GÉRANT
Rôle avancé nécessitant un abonnement.

Permet de :
• Créer et gérer des projets illimités
• Publier des offres et missions
• Recruter des collaborateurs
• Gérer les finances du projet
• Créer une boutique de services
• Accéder au tableau de bord auteur complet

🔄 Cumul des rôles :
Un même compte peut être utilisateur ET auteur simultanément. Vous pouvez donc gérer vos projets tout en postulant à d'autres opportunités.`
      },
      {
        title: "Naviguer sur la plateforme",
        content: `Voici les principales sections de DJAMANN :

🏠 Accueil
Page d'atterrissage présentant la plateforme.

📋 Pour Toi (Feed)
Le cœur de DJAMANN ! Ici vous trouverez :
• Toutes les offres publiées (emploi, stage, collaboration, prestation, partenariat)
• Filtres par type, catégorie
• Recherche intelligente
• Tri par pertinence, date

👤 Mon Profil
Gérez vos informations personnelles :
• Photo et description
• Compétences
• Historique des candidatures
• Paramètres du compte

✍️ Espace Auteur
Si vous êtes auteur :
• Tableau de bord complet
• Gestion des projets
• Suivi des candidatures reçues
• Finances et statistiques

🛍️ Boutique
Accès aux boutiques publiques et à la vôtre si vous en avez une.`
      },
      {
        title: "Compléter son profil",
        content: `Un profil complet augmente vos chances d'être sélectionné !

📸 Photo de profil
• Utilisez une photo professionnelle
• Visage visible et souriant
• Bonne qualité d'image

📝 Description / Bio
• Présentez-vous en quelques lignes
• Mettez en avant vos points forts
• Soyez authentique

🛠️ Compétences
• Listez vos compétences principales
• Soyez précis (ex: "React.js" plutôt que "Programmation")
• Ordonnez par niveau de maîtrise

📍 Localisation
• Indiquez votre ville
• Précisez si vous pouvez vous déplacer
• Mentionnez si vous travaillez à distance

📞 Contact
• Numéro de téléphone (visible uniquement par vos collaborateurs)
• Liens vers vos réseaux professionnels (LinkedIn, portfolio...)

💡 Conseil : Mettez à jour régulièrement votre profil avec vos nouvelles expériences et compétences.`
      }
    ],
    'user': [
      {
        title: "Trouver des opportunités",
        content: `Le feed "Pour Toi" est votre meilleur allié pour trouver des opportunités :

🔍 Recherche intelligente
• Tapez des mots-clés dans la barre de recherche
• La recherche analyse : titre, description, compétences, tags, localisation
• Suggestions automatiques basées sur votre profil

🎯 Filtres disponibles
• Type d'offre : Emploi, Stage, Collaboration, Prestation, Partenariat
• Catégorie : Tech, Commerce, Services, Agriculture, etc.
• Localisation : Par ville ou région

📊 Options de tri
• Aléatoire : Découvrez de nouvelles opportunités
• Plus récent : Les dernières offres en premier
• Plus ancien : Les offres publiées depuis longtemps

💡 Astuces :
• Consultez le feed régulièrement, de nouvelles offres arrivent chaque jour
• Utilisez les tags pour affiner votre recherche
• Sauvegardez les offres qui vous intéressent`
      },
      {
        title: "Postuler à une offre",
        content: `Vous avez trouvé une offre intéressante ? Voici comment postuler :

📋 Avant de postuler
1. Lisez attentivement la description complète
2. Vérifiez que vous correspondez au profil recherché
3. Assurez-vous de comprendre les conditions (rémunération, durée, etc.)

✍️ Soumettre votre candidature
1. Cliquez sur le bouton "Postuler"
2. Rédigez un message de motivation personnalisé
3. Mettez en avant vos compétences pertinentes
4. Joignez des documents si demandé (CV, portfolio)
5. Validez votre candidature

📧 Après la candidature
• Vous recevrez une confirmation par notification
• Le gérant du projet examinera votre profil
• Vous serez notifié de la décision (accepté/refusé)

💡 Conseils pour une bonne candidature :
• Personnalisez chaque message (pas de copier-coller)
• Montrez que vous avez compris le projet
• Soyez concis mais percutant
• Mentionnez des réalisations concrètes similaires`
      },
      {
        title: "Suivre ses candidatures",
        content: `Gardez un œil sur vos candidatures :

📊 États possibles
• En attente : Le gérant n'a pas encore examiné votre candidature
• Acceptée : Félicitations ! Vous êtes retenu pour le projet
• Refusée : Le gérant a choisi un autre profil
• Annulée : Vous avez retiré votre candidature

📍 Où voir mes candidatures ?
• Profil → Mes candidatures
• Notification push/email pour chaque changement de statut

✅ Candidature acceptée
Quand votre candidature est acceptée :
1. Vous recevez une notification
2. Le gérant vous contactera avec les détails
3. Discutez des conditions précises
4. Commencez votre collaboration !

❌ Candidature refusée
Ne vous découragez pas !
• Demandez un feedback si possible
• Améliorez votre profil
• Continuez à postuler à d'autres offres
• Une candidature refusée ≠ échec personnel`
      },
      {
        title: "Devenir auteur",
        content: `Vous voulez créer vos propres projets ? Devenez auteur !

🎯 Pourquoi devenir auteur ?
• Créer et gérer vos propres projets
• Recruter des collaborateurs
• Créer une boutique de services
• Accéder aux outils de gestion avancés

💳 Comment devenir auteur ?
1. Allez dans votre profil ou le menu principal
2. Cliquez sur "Devenir auteur" ou "Espace auteur"
3. Choisissez un abonnement (modules à activer)
4. Procédez au paiement
5. Votre compte passe en mode auteur !

📦 Système modulaire
• Vous ne payez que les modules dont vous avez besoin
• Activez/désactivez des modules à tout moment
• Prix transparent par module

💡 Conseil : Commencez avec les modules essentiels, puis ajoutez-en au fur et à mesure de vos besoins.`
      }
    ],
    'author': [
      {
        title: "Le tableau de bord auteur",
        content: `En tant qu'auteur, vous avez accès à un tableau de bord complet :

📊 Vue d'ensemble
• Statistiques de vos projets
• Nombre de candidatures reçues
• Revenus et dépenses
• Activité récente

📁 Mes Projets
• Liste de tous vos projets
• Statut de chaque projet (actif, en pause, terminé)
• Accès rapide à la gestion

👥 Mon Équipe
• Tous vos collaborateurs
• Statuts et rôles assignés
• Gestion des paiements

💰 Finances
• Revenus par projet
• Dépenses et paiements
• Commission DJAMANN (3%)
• Historique des transactions

📈 Rapports
• Statistiques détaillées
• Évolution dans le temps
• Export des données`
      },
      {
        title: "Créer un projet",
        content: `Créer un projet est la première étape pour construire quelque chose de grand !

📝 Informations requises
1. Nom du projet
   • Court et mémorable
   • Reflète l'activité

2. Description
   • Expliquez clairement votre vision
   • Objectifs à court et long terme
   • Ce qui rend votre projet unique

3. Catégorie
   • Tech, Commerce, Services, Agriculture, etc.
   • Aide les utilisateurs à vous trouver

4. Logo/Image (optionnel)
   • Image représentative
   • Format carré recommandé

🚀 Après création
• Votre projet est créé mais pas encore visible publiquement
• Ajoutez des offres pour recruter
• Configurez votre équipe
• Publiez quand vous êtes prêt

💡 Conseil : Prenez le temps de bien décrire votre projet. Une bonne description attire les meilleurs talents !`
      },
      {
        title: "Publier une offre",
        content: `Recrutez les bonnes personnes pour votre projet :

📋 Types d'offres disponibles

🤝 Collaboration (Associé)
• Recherche de co-fondateurs
• Partage des responsabilités et revenus
• Engagement long terme

💰 Prestation (Freelance)
• Mission ponctuelle
• Livrable défini
• Paiement à la tâche

👔 Emploi
• Poste salarié
• Contrat classique
• Rémunération mensuelle

🎓 Stage
• Pour étudiants/apprenants
• Durée limitée
• Formation incluse

🤝 Partenariat
• Collaboration inter-entreprises
• Sponsors
• Échanges de services

📝 Créer une offre
1. Sélectionnez le projet concerné
2. Choisissez le type d'offre
3. Rédigez un titre accrocheur
4. Décrivez la mission en détail
5. Listez les compétences requises
6. Définissez la rémunération/conditions
7. Publiez !

💡 Conseils :
• Soyez précis sur les attentes
• Indiquez clairement la rémunération
• Mentionnez les avantages (flexibilité, formation, etc.)`
      },
      {
        title: "Gérer les candidatures",
        content: `Des candidatures arrivent ? Voici comment les gérer efficacement :

📥 Réception des candidatures
• Notification pour chaque nouvelle candidature
• Accès via le tableau de bord
• Vue d'ensemble par offre

👀 Examiner une candidature
Pour chaque candidat, vous voyez :
• Profil complet (photo, bio, compétences)
• Message de motivation
• Historique sur DJAMANN
• Documents joints

✅ Accepter une candidature
1. Examinez attentivement le profil
2. Cliquez sur "Accepter"
3. Le candidat est notifié
4. Il rejoint votre équipe
5. Contactez-le pour les détails

❌ Refuser une candidature
1. Cliquez sur "Refuser"
2. Optionnel : ajoutez un message explicatif
3. Le candidat est notifié

💡 Bonnes pratiques :
• Répondez rapidement (les bons profils partent vite !)
• Donnez un feedback constructif si possible
• Vérifiez bien les compétences annoncées
• N'hésitez pas à poser des questions`
      },
      {
        title: "Gérer son équipe",
        content: `Une fois vos collaborateurs recrutés, gérez-les efficacement :

👥 Vue équipe
Accédez à la liste complète de vos collaborateurs :
• Photo et nom
• Rôle dans le projet
• Date d'arrivée
• Statut (actif, en pause)

📋 Assigner des tâches
1. Créez une tâche décrivant le travail à faire
2. Sélectionnez le collaborateur concerné
3. Définissez une deadline
4. Suivez l'avancement

💰 Paiements
• Enregistrez les paiements effectués
• Suivez ce qui est dû à chacun
• Gardez un historique clair

📊 Performance
• Tâches complétées
• Délais respectés
• Qualité du travail

🔄 Fin de collaboration
• Option de retirer un collaborateur
• Gardez une trace de l'historique
• Respectez les engagements pris`
      }
    ],
    'projects': [
      {
        title: "Structure d'un projet",
        content: `Un projet DJAMANN se compose de plusieurs éléments :

📁 Informations générales
• Nom du projet
• Description détaillée
• Catégorie (Tech, Commerce, etc.)
• Logo/Image
• Statut (actif, en pause, terminé)

📢 Offres/Missions
• Liste des postes à pourvoir
• Types variés (emploi, collaboration, prestation...)
• Conditions et rémunération

👥 Équipe
• Auteur/Gérant (vous)
• Collaborateurs recrutés
• Rôles de chacun

✅ Tâches
• Liste des travaux à réaliser
• Assignations aux collaborateurs
• Suivi de l'avancement
• Deadlines

💰 Finances
• Revenus générés
• Dépenses enregistrées
• Paiements aux collaborateurs
• Commission DJAMANN (3%)

📊 Statistiques
• Performance globale
• Évolution dans le temps`
      },
      {
        title: "Catégories de projets",
        content: `DJAMANN propose plusieurs catégories pour classer votre projet :

💻 Tech
Projets liés à la technologie, développement web/mobile, IA, etc.

🛒 Commerce
Vente de produits, e-commerce, import/export.

🔧 Services
Prestations de services aux particuliers ou entreprises.

🌾 Agriculture
Projets agricoles, agroalimentaire, élevage.

🏠 Immobilier
Promotion immobilière, gestion locative, construction.

🚚 Transport
Logistique, livraison, transport de personnes.

🍽️ Food / Restauration
Restaurants, food trucks, traiteurs, cuisine.

👗 Mode
Vêtements, accessoires, cosmétiques.

💊 Santé
Services médicaux, bien-être, pharmacie.

📚 Éducation
Formation, e-learning, soutien scolaire.

💰 Finance
Services financiers, fintech, comptabilité.

📁 Autre
Tout projet ne rentrant pas dans les catégories ci-dessus.`
      },
      {
        title: "Cycle de vie d'un projet",
        content: `Un projet passe par plusieurs phases :

🌱 1. Création
• Définition du projet
• Rédaction de la description
• Choix de la catégorie

📢 2. Recrutement
• Publication des offres
• Réception des candidatures
• Sélection des collaborateurs

🚀 3. Lancement
• Équipe constituée
• Tâches assignées
• Travail en cours

📈 4. Développement
• Exécution des tâches
• Suivi de l'avancement
• Gestion quotidienne
• Ajustements si nécessaire

💰 5. Croissance
• Revenus générés
• Expansion de l'équipe
• Nouveaux services/produits

🔄 6. Évolution
• Pivots si nécessaire
• Nouvelles orientations
• Scaling

✅ 7. Maturité
• Projet établi
• Revenus stables
• Équipe rodée`
      }
    ],
    'tasks': [
      {
        title: "Comprendre les tâches",
        content: `Les tâches sont au cœur de la gestion de projet :

📋 Qu'est-ce qu'une tâche ?
Une tâche représente un travail spécifique à réaliser dans le cadre d'un projet. Elle permet de :
• Décomposer un projet en actions concrètes
• Assigner du travail aux collaborateurs
• Suivre l'avancement
• Mesurer la performance

📝 Éléments d'une tâche
• Titre : description courte et claire
• Description : détails du travail à effectuer
• Assigné à : collaborateur responsable
• Deadline : date limite de réalisation
• Priorité : urgent, normal, faible
• Statut : à faire, en cours, terminé

🎯 Types de tâches
• Développement : création de fonctionnalités
• Design : création visuelle
• Contenu : rédaction, médias
• Marketing : promotion, communication
• Admin : tâches administratives
• Autre : tout le reste`
      },
      {
        title: "Créer et assigner des tâches",
        content: `En tant qu'auteur, vous pouvez créer des tâches pour votre équipe :

📝 Créer une tâche
1. Allez dans votre projet
2. Section "Tâches" ou "Missions"
3. Cliquez sur "Nouvelle tâche"
4. Remplissez les informations :
   • Titre clair et précis
   • Description détaillée
   • Deadline réaliste
   • Priorité appropriée

👤 Assigner à un collaborateur
1. Sélectionnez le collaborateur
2. Vérifiez sa charge de travail actuelle
3. Confirmez l'assignation
4. Le collaborateur est notifié

💡 Bonnes pratiques :
• Une tâche = un livrable clair
• Évitez les tâches trop larges
• Soyez précis dans la description
• Donnez les ressources nécessaires
• Fixez des deadlines réalistes`
      },
      {
        title: "Suivre l'avancement",
        content: `Gardez un œil sur la progression de votre projet :

📊 Vue globale
Le tableau de bord affiche :
• Nombre de tâches par statut
• Tâches en retard
• Progression globale en %

📋 Statuts des tâches
• À faire : Pas encore commencée
• En cours : Travail en progression
• En révision : Soumise pour validation
• Terminée : Validée et complétée
• Bloquée : Problème empêchant l'avancement

🔔 Notifications
Recevez des alertes pour :
• Changement de statut
• Tâche terminée
• Deadline approchante
• Tâche en retard

💡 Conseils de suivi :
• Vérifiez le tableau de bord quotidiennement
• Communiquez avec les collaborateurs
• Identifiez les blocages rapidement
• Ajustez les deadlines si nécessaire`
      }
    ],
    'shop': [
      {
        title: "Créer sa boutique",
        content: `La boutique vous permet de vendre vos services directement :

🛍️ Qu'est-ce que la boutique ?
C'est votre vitrine professionnelle sur DJAMANN :
• Présentez vos services
• Recevez des commandes
• Gérez vos clients
• Accessible publiquement via un lien unique

📝 Configurer sa boutique
1. Allez dans votre espace auteur
2. Section "Ma Boutique"
3. Remplissez les informations :
   • Nom de la boutique
   • Description
   • Logo
   • Slug personnalisé (ex: /boutique/mon-entreprise)

🌐 Lien public
Votre boutique est accessible via :
• votresite.com/boutique/votre-slug
• Partagez ce lien partout !
• QR Code générable

💡 Conseils :
• Choisissez un nom mémorable
• Rédigez une description attractive
• Utilisez un logo professionnel
• Mettez régulièrement à jour vos services`
      },
      {
        title: "Ajouter des services",
        content: `Proposez vos services à la vente :

📋 Créer un service
1. Dans votre boutique, cliquez "Nouveau service"
2. Remplissez :
   • Nom du service
   • Description détaillée
   • Prix
   • Délai de livraison
   • Image représentative

💰 Options de tarification
• Prix fixe : un montant défini
• À partir de : prix de base avec options
• Sur devis : prix personnalisé selon la demande

📸 Visuels
• Ajoutez des images de qualité
• Montrez des exemples de réalisations
• Format recommandé : carré ou 16:9

🏷️ Organisation
• Classez par catégorie
• Utilisez des tags pertinents
• Mettez en avant les best-sellers

💡 Conseils :
• Prix clairs et transparents
• Descriptions précises
• Délais réalistes
• Mettez à jour régulièrement`
      },
      {
        title: "Gérer les demandes de services",
        content: `Des clients vous contactent ? Voici comment gérer leurs demandes :

📥 Types de demandes
• Commande directe : Client achète un service existant
• Demande personnalisée : Client décrit un besoin spécifique

📋 Demande personnalisée
Quand un client soumet une demande :
1. Vous recevez une notification
2. Consultez les détails de la demande
3. Évaluez si vous pouvez répondre
4. Proposez un devis ou acceptez/refusez

✅ Accepter une demande
1. Examinez la demande
2. Cliquez "Accepter"
3. Définissez le prix si personnalisé
4. Contactez le client pour les détails
5. Réalisez le travail
6. Marquez comme terminé

❌ Refuser une demande
• Si vous ne pouvez pas répondre
• Expliquez courtoisement pourquoi
• Suggérez des alternatives si possible

💡 Bonnes pratiques :
• Répondez rapidement
• Soyez transparent sur les délais
• Communiquez régulièrement
• Livrez un travail de qualité`
      }
    ],
    'finance': [
      {
        title: "Comprendre les finances",
        content: `DJAMANN vous aide à gérer les finances de vos projets :

💰 Revenus
Enregistrez toutes les entrées d'argent :
• Ventes de services
• Paiements clients
• Autres revenus

📉 Dépenses
Suivez vos sorties d'argent :
• Achats de matériel
• Services externes
• Frais divers

💳 Paiements collaborateurs
Gérez la rémunération de votre équipe :
• Salaires
• Parts de revenus
• Primes

📊 Commission DJAMANN
• 3% des revenus mensuels
• Prélevée automatiquement
• Contribue au maintien de la plateforme

🔄 Calcul automatique
DJAMANN calcule automatiquement :
• Bénéfices (Revenus - Dépenses)
• Commission due
• Solde disponible`
      },
      {
        title: "Enregistrer des transactions",
        content: `Gardez vos finances à jour :

➕ Ajouter un revenu
1. Section "Finances" de votre projet
2. Cliquez "Nouveau revenu"
3. Renseignez :
   • Montant
   • Description/Source
   • Date
   • Catégorie
4. Validez

➖ Ajouter une dépense
1. Cliquez "Nouvelle dépense"
2. Renseignez :
   • Montant
   • Description
   • Date
   • Catégorie
4. Joignez un justificatif si nécessaire
5. Validez

📅 Régularité
• Enregistrez les transactions au fur et à mesure
• Évitez d'accumuler du retard
• Vérifiez mensuellement vos comptes

💡 Conseil : Plus vos finances sont à jour, plus vous avez une vision claire de la santé de votre projet.`
      },
      {
        title: "Payer ses collaborateurs",
        content: `Rémunérez votre équipe de manière transparente :

💰 Modes de paiement
DJAMANN accepte :
• Mobile Money (Orange, MTN, Wave, Moov)
• Cartes bancaires
• Virements bancaires

📋 Enregistrer un paiement
1. Allez dans la fiche du collaborateur
2. Section "Paiements"
3. Cliquez "Nouveau paiement"
4. Renseignez :
   • Montant
   • Motif (salaire, prime, part...)
   • Date
5. Validez

📊 Historique
• Tous les paiements sont enregistrés
• Export possible pour comptabilité
• Visible par le collaborateur concerné

⚖️ Bonnes pratiques :
• Payez à temps vos collaborateurs
• Soyez transparent sur les conditions
• Gardez des preuves de paiement
• Respectez vos engagements`
      },
      {
        title: "Rapports financiers",
        content: `Analysez la performance financière de vos projets :

📊 Types de rapports
• Résumé mensuel : Vue globale du mois
• Par projet : Finances détaillées par projet
• Par collaborateur : Historique des paiements
• Annuel : Bilan de l'année

📈 Métriques clés
• Chiffre d'affaires total
• Dépenses totales
• Bénéfice net
• Commission DJAMANN (3%)
• Évolution mois par mois

📥 Export
• Format PDF pour archivage
• Format Excel pour analyse
• Utile pour la comptabilité

📅 Fréquence recommandée
• Vérification hebdomadaire
• Rapport mensuel détaillé
• Bilan annuel complet`
      }
    ],
    'subscription': [
      {
        title: "Système d'abonnement",
        content: `DJAMANN fonctionne avec un système modulaire innovant :

📦 Concept
Au lieu d'un abonnement unique, vous choisissez les modules dont vous avez besoin :
• Payez uniquement ce que vous utilisez
• Ajoutez des modules à tout moment
• Retirez des modules inutilisés

🧩 Modules disponibles (exemples)
• Gestion de projets
• Gestion des finances
• Boutique et services
• Gestion des collaborateurs
• Rapports avancés
• Et plus encore...

💰 Tarification
• Chaque module a son prix mensuel
• Tarifs affichés dans la section "Abonnements"
• Possibilité d'extension de durée

🎁 Modules gratuits
Certains modules de base peuvent être offerts pour permettre à tous de démarrer leur aventure entrepreneuriale.`
      },
      {
        title: "Activer des modules",
        content: `Personnalisez votre abonnement :

📋 Comment activer un module
1. Allez dans "Abonnements" ou "Mes modules"
2. Parcourez les modules disponibles
3. Cliquez sur un module pour voir les détails
4. Cliquez "Activer"
5. Procédez au paiement
6. Le module est actif immédiatement !

💳 Paiement
• Mobile Money (Orange, MTN, Wave, Moov)
• Cartes bancaires
• Paiement sécurisé

📅 Durée
• Standard : 1 mois
• Possibilité d'étendre (3, 6, 12 mois)
• Réductions possibles sur les durées longues

🔄 Renouvellement
• Notification avant expiration
• Renouvellement manuel ou automatique
• Possibilité d'annuler à tout moment`
      },
      {
        title: "Gérer ses modules",
        content: `Gardez le contrôle de vos abonnements :

📊 Vue d'ensemble
Dans "Mes abonnements", vous voyez :
• Modules actifs
• Date d'expiration de chacun
• Modules disponibles à activer

🔄 Renouveler un module
1. Avant expiration, vous êtes notifié
2. Cliquez sur le module concerné
3. Choisissez la durée de renouvellement
4. Procédez au paiement

⏸️ Ne pas renouveler
• Si vous n'avez plus besoin d'un module
• Laissez-le simplement expirer
• Vos données restent accessibles en lecture
• Vous pouvez réactiver plus tard

📈 Optimiser son abonnement
• Analysez les modules que vous utilisez vraiment
• Désactivez ceux inutilisés
• Profitez des offres sur les durées longues

💡 Conseil : Commencez petit et ajoutez des modules selon l'évolution de vos besoins.`
      },
      {
        title: "Commission plateforme",
        content: `Comprendre la commission DJAMANN :

📊 Taux de commission
• 3% des revenus mensuels de vos projets
• Applicable uniquement aux auteurs/gérants
• Calculée automatiquement

🧮 Comment c'est calculé
Commission = (Total des revenus du mois) × 3%

Exemple :
• Revenus du mois : 500 000 FCFA
• Commission : 500 000 × 3% = 15 000 FCFA

📅 Prélèvement
• Calculé en fin de mois
• Notification du montant dû
• À régler avant le 10 du mois suivant

🎯 À quoi sert la commission ?
• Maintenance de la plateforme
• Développement de nouvelles fonctionnalités
• Support utilisateur
• Sécurité des données
• Hébergement des serveurs

💡 Note : Les transactions internes (paiements aux collaborateurs) ne sont pas soumises à la commission de 3%.`
      }
    ],
    'faq': [
      {
        title: "Questions générales",
        content: `❓ DJAMANN est-il gratuit ?
L'inscription et l'utilisation de base sont gratuites. Pour créer des projets et accéder aux fonctionnalités auteur, un abonnement modulaire est nécessaire.

❓ Quel est l'âge minimum ?
16 ans. Les mineurs de 16-18 ans doivent avoir l'accord parental.

❓ DJAMANN est disponible où ?
Actuellement en Côte d'Ivoire. Extension vers d'autres pays africains prévue.

❓ Comment contacter le support ?
• Email : support@djamann.com
• Formulaire de contact sur le site
• Chat intégré (si disponible)

❓ Mes données sont-elles sécurisées ?
Oui ! Nous utilisons le chiffrement, des connexions sécurisées (HTTPS), et ne stockons jamais vos données bancaires.`
      },
      {
        title: "Questions sur les projets",
        content: `❓ Combien de projets puis-je créer ?
Illimité ! Tant que vous avez un abonnement actif.

❓ Puis-je mettre un projet en pause ?
Oui, vous pouvez changer le statut d'un projet à tout moment.

❓ Que se passe-t-il si mon abonnement expire ?
Vos projets restent visibles mais vous ne pouvez plus les modifier ni en créer de nouveaux. Réactivez votre abonnement pour récupérer l'accès complet.

❓ Puis-je transférer un projet à quelqu'un d'autre ?
Cette fonctionnalité n'est pas encore disponible. Contactez le support pour les cas particuliers.

❓ Comment supprimer un projet ?
Dans les paramètres du projet, option "Supprimer". Attention : action irréversible !`
      },
      {
        title: "Questions sur les paiements",
        content: `❓ Quels moyens de paiement sont acceptés ?
• Mobile Money : Orange Money, MTN Money, Wave, Moov Money
• Cartes bancaires : Visa, Mastercard
• Virements bancaires

❓ Comment fonctionne la commission de 3% ?
Elle s'applique aux revenus mensuels de vos projets. Calculée automatiquement et à régler en début de mois suivant.

❓ Les paiements aux collaborateurs sont-ils sécurisés ?
Oui, nous utilisons des partenaires de paiement certifiés.

❓ Puis-je obtenir un remboursement ?
Les abonnements ne sont généralement pas remboursables. Contactez le support pour les cas exceptionnels.

❓ Comment retirer mes gains ?
Via la section "Finances" de votre projet, option "Retrait". Délai de 24-72h ouvrées.`
      },
      {
        title: "Questions sur la boutique",
        content: `❓ Ma boutique est-elle visible par tous ?
Oui, les boutiques sont publiques et accessibles via leur lien unique.

❓ Combien de services puis-je ajouter ?
Illimité !

❓ Puis-je avoir plusieurs boutiques ?
Une boutique par projet. Si vous avez plusieurs projets, chacun peut avoir sa boutique.

❓ Comment les clients me contactent-ils ?
Via le formulaire de demande de service ou les coordonnées affichées sur votre boutique.

❓ Puis-je personnaliser l'apparence de ma boutique ?
Vous pouvez ajouter logo, description, et organiser vos services. Plus d'options de personnalisation sont prévues.`
      },
      {
        title: "Problèmes et dépannage",
        content: `🔧 Je ne peux pas me connecter
• Vérifiez votre email et mot de passe
• Utilisez "Mot de passe oublié"
• Effacez les cookies de votre navigateur
• Essayez un autre navigateur

🔧 Je ne reçois pas les notifications
• Vérifiez vos paramètres de notification
• Vérifiez votre dossier spam
• Autorisez les notifications push

🔧 Le paiement a échoué
• Vérifiez le solde de votre compte
• Essayez un autre moyen de paiement
• Contactez votre opérateur mobile/banque

🔧 Mon module n'est pas activé
• Vérifiez que le paiement a été effectué
• Attendez quelques minutes (délai de traitement)
• Rafraîchissez la page
• Contactez le support si le problème persiste

🔧 Autre problème ?
Contactez le support : support@djamann.com`
      }
    ]
  };

  // Get current sections based on active category
  const currentSections = allSections[activeCategory] || [];

  // Filter sections by search query
  const filteredSections = searchQuery
    ? Object.entries(allSections).flatMap(([category, sections]) =>
        sections
          .filter(s => 
            s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.content.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map(s => ({ ...s, category }))
      )
    : currentSections;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">📖</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Guide Utilisateur</h1>
              <p className="text-white/80">Tout ce que vous devez savoir sur DJAMANN</p>
            </div>
          </div>
          
          {/* Search bar */}
          <div className="mt-8 relative max-w-xl">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher dans le guide..."
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30 transition-all"
            />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          {!searchQuery && (
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sticky top-6">
                <h2 className="font-bold text-gray-800 mb-4 px-2">Catégories</h2>
                <nav className="space-y-1">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        setExpandedSection(null);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition-all ${
                        activeCategory === cat.id
                          ? 'bg-emerald-100 text-emerald-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xl">{cat.icon}</span>
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1">
            {/* Search results header */}
            {searchQuery && (
              <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-emerald-700">
                  <span className="font-medium">{filteredSections.length}</span> résultat(s) pour "{searchQuery}"
                </p>
              </div>
            )}

            {/* Category title */}
            {!searchQuery && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                  <span className="text-3xl">{categories.find(c => c.id === activeCategory)?.icon}</span>
                  {categories.find(c => c.id === activeCategory)?.label}
                </h2>
              </div>
            )}

            {/* Sections */}
            <div className="space-y-4">
              {filteredSections.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <span className="text-6xl mb-4 block">🔍</span>
                  <h3 className="text-xl font-bold text-gray-700 mb-2">Aucun résultat</h3>
                  <p className="text-gray-500">Essayez avec d'autres mots-clés</p>
                </div>
              ) : (
                filteredSections.map((section, index) => (
                  <div 
                    key={index}
                    className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 ${
                      expandedSection === index ? 'border-emerald-300 shadow-md' : 'border-gray-100 hover:shadow-md'
                    }`}
                  >
                    <button
                      onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                      className="w-full p-6 text-left flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3">
                        {searchQuery && section.category && (
                          <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">
                            {categories.find(c => c.id === section.category)?.label}
                          </span>
                        )}
                        <h3 className="text-lg font-semibold text-gray-800">{section.title}</h3>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                          expandedSection === index ? 'rotate-180' : ''
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${
                      expandedSection === index ? 'max-h-[3000px]' : 'max-h-0'
                    }`}>
                      <div className="px-6 pb-6 text-gray-600 leading-relaxed whitespace-pre-line border-t border-gray-100 pt-4">
                        {section.content}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick links */}
        <div className="mt-16 text-center">
          <h3 className="text-lg font-bold text-gray-800 mb-6">Besoin d'autre chose ?</h3>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link 
              to="/terms" 
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              📜 Conditions d'utilisation
            </Link>
            <Link 
              to="/privacy" 
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              🔒 Politique de confidentialité
            </Link>
            <Link 
              to="/register" 
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-colors font-medium"
            >
              🚀 Créer un compte
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guide;
